/**
 * Single entry point behind `useRouteOperation`: runs the strategy's steps in order, returns step
 * errors as `OperationResult.error` instead of throwing, then invalidates per `defaultInvalidatePolicy`.
 */

import { isErr, isOk } from 'types/entities/shared/result'
import { DEFAULT_ROUTE_OPS_STRATEGY } from 'types/modules/routing/Strategy'

import { extractBackendError } from './backendError'
import { createConsoleLogger } from './logger'
import { makeInvalidateStep } from './steps/invalidate'
import { v1BulkStrategy, v2PipelineStrategy, v3SlotStrategy, type RouteOpStrategy } from './strategies'

import type { StepResult } from './types'
import type { Result } from 'types/entities/shared/result'
import type {
  Deps,
  OperationPlan,
  OperationResult,
  Phase,
  PipelineContext,
  RouteOpError,
  RouteOpIntent,
  RouteOpsStrategy,
  VerifyReport,
} from 'types/modules/routing'

// Read-only phases: nothing the server keeps changes, so they cannot leave a cache stale.
const READ_ONLY_PHASES: ReadonlySet<Phase> = new Set<Phase>(['PREFLIGHT', 'VERIFY', 'INVALIDATE'])

const STRATEGY_REGISTRY: Readonly<Record<RouteOpsStrategy, RouteOpStrategy>> = {
  'v2-pipeline': v2PipelineStrategy,
  'v1-bulk': v1BulkStrategy,
  'v3-slot': v3SlotStrategy,
}

/**
 * When the executor invalidates RTKQ caches. `'on-success'` only when every phase resolved
 * ok, `'on-commit'` also after a failure that already committed; `'never'`/`'always'` for tests.
 */
export type InvalidatePolicy = 'never' | 'on-success' | 'on-commit' | 'always'

/** Only a multi-call v3 plan can abort part-committed; v1/v2 callers compensate themselves. */
const defaultInvalidatePolicy = (strategyId: RouteOpsStrategy): InvalidatePolicy => (strategyId === 'v3-slot' ? 'on-commit' : 'on-success')

/**
 * Caller-overridable knobs. Production wires the defaults from a central
 * config module; tests / dev tooling override per call.
 */
export interface ExecuteOptions {
  /** Override the strategy choice. Default: `persistentRouteOpsStrategySlice` value. */
  readonly strategyId?: RouteOpsStrategy
  /** `'strict'` aborts on any preflight error; `'lenient'` warns and continues. */
  readonly preflightPolicy?: 'strict' | 'lenient'
  /** `'always'` runs the verifier; `'never'` skips it (used in unit tests). */
  readonly verifyPolicy?: 'always' | 'never'
  /** UUID generator override — defaults to `crypto.randomUUID()`. */
  readonly mintVersionContext?: () => string
  /** Cache-invalidation policy. Defaults per strategy — see `defaultInvalidatePolicy`. */
  readonly debug?: { readonly invalidate?: InvalidatePolicy }
  /**
   * **Test-only** plan injection. Tests pass a pre-built `OperationPlan` so
   * the executor's tail behaviour (invalidation) can be exercised without a
   * real pipeline. Production code never sets this.
   */
  readonly __planForTests?: OperationPlan
}

/**
 * Single sanctioned entry point. Returns errors as data — never throws on a BE failure
 * or a business rejection. The tail invalidates per `defaultInvalidatePolicy`.
 */
export const executeOperation = async (intent: RouteOpIntent, deps: Deps, options?: ExecuteOptions): Promise<OperationResult> => {
  const strategyId: RouteOpsStrategy = options?.strategyId ?? DEFAULT_ROUTE_OPS_STRATEGY
  const invalidatePolicy: InvalidatePolicy = options?.debug?.invalidate ?? defaultInvalidatePolicy(strategyId)
  const strategy = STRATEGY_REGISTRY[strategyId]
  // eslint-disable-next-line no-underscore-dangle -- the dunder keeps a test-only override from reading as a real option
  const plan: OperationPlan = options?.__planForTests ?? strategy.plan(intent, deps)
  const logger = createConsoleLogger()
  // BE deserializes `versionContext` as a UUID on every v2 endpoint — sending
  // a non-UUID string fails Jackson with HttpMessageNotReadableException (400).
  // The literal default below is only valid as a UUID, never as a placeholder.
  const versionContext = options?.mintVersionContext?.() ?? crypto.randomUUID()

  logger.debug(`▶ ${intent.kind} via ${strategyId}`, {
    intent,
    versionContext,
    plan: plan.steps.map(s => ({ phase: s.phase, id: s.id })),
  })

  // Steps report each landed call, so dying on batch two of one step still counts as committed.
  let hasCommitted = false

  const ctx: PipelineContext = {
    versionContext,
    intent,
    deps,
    results: new Map<string, unknown>(),
    logger,
    recordCommit: () => {
      hasCommitted = true
    },
  }

  const executedPhases: Array<{ readonly phase: Phase; readonly ms: number }> = []
  let firstError: { phase: Phase; stepId: string; cause: RouteOpError } | undefined
  // The VERIFY step's own report. Nothing else reads a step's value, so without this the toast
  // renders "0 checked" however much the verifier actually compared.
  let verifyReport: VerifyReport | undefined

  // eslint-disable-next-line no-restricted-syntax -- steps run in order and each awaits the last; an array method would fan them out
  for (const step of plan.steps) {
    const start = Date.now()
    logger.debug(`→ ${step.phase} (${step.id})`)
    let stepResult: Result<unknown, RouteOpError>
    try {
      // eslint-disable-next-line no-await-in-loop -- sequential by design, see the loop
      stepResult = await step.run(ctx)
    } catch (cause: unknown) {
      const { status, message } = extractBackendError(cause)
      stepResult = {
        ok: false,
        error: { kind: 'BE_REJECTED', phase: step.phase, status, message },
      }
    }
    const ms = Date.now() - start
    executedPhases.push({ phase: step.phase, ms })
    if (isErr(stepResult)) {
      logger.error(`✗ ${step.phase} (${ms}ms) ${stepResult.error.kind}`, stepResult.error)
      firstError = { phase: step.phase, stepId: step.id, cause: stepResult.error }
      break
    }
    if (!READ_ONLY_PHASES.has(step.phase)) hasCommitted = true
    if (step.phase === 'VERIFY' && isOk(stepResult)) {
      verifyReport = (stepResult.value as StepResult<'VERIFY', VerifyReport>).value
    }
    logger.debug(`✓ ${step.phase} (${ms}ms)`, isOk(stepResult) ? stepResult.value : undefined)
  }

  const succeeded = !firstError

  // A part-committed v3 plan leaves the caches stale whether or not the tail step failed.
  const shouldInvalidate =
    invalidatePolicy === 'always' ||
    (invalidatePolicy === 'on-success' && succeeded) ||
    (invalidatePolicy === 'on-commit' && (succeeded || hasCommitted))
  if (shouldInvalidate) {
    const invalidateStep = makeInvalidateStep({ intent }, deps)
    const invalidateStart = Date.now()
    const invalidateResult = await invalidateStep.run(ctx)
    const ms = Date.now() - invalidateStart
    executedPhases.push({ phase: 'INVALIDATE', ms })
    if (isOk(invalidateResult)) {
      logger.debug(`✓ INVALIDATE (${ms}ms)`, invalidateResult.value.value)
    }
  }

  if (firstError) {
    logger.error(`■ ${intent.kind} FAILED at ${firstError.phase}`, firstError.cause)
    return {
      success: false,
      executedPhases,
      committedSequences: [],
      error: firstError,
      cachesInvalidated: shouldInvalidate,
      verifyReport,
    }
  }
  logger.debug(`■ ${intent.kind} OK — ${executedPhases.length} phase(s)`)
  return {
    success: true,
    executedPhases,
    committedSequences: [],
    cachesInvalidated: shouldInvalidate,
    verifyReport,
  }
}
